import * as React from 'react'

import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import ListItem from '@mui/material/ListItem'
import List from '@mui/material/List'

import CircularProgress from '@mui/material/CircularProgress'

import Tooltip from '@mui/material/Tooltip'

import Divider from '@mui/material/Divider'
import TextField from '@mui/material/TextField'
import IconButton from '@mui/material/IconButton'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import InputLabel from '@mui/material/InputLabel'
import InputAdornment from '@mui/material/InputAdornment'

import AddCircleIcon from '@mui/icons-material/AddCircleOutlined'
import GrainOutlinedIcon from '@mui/icons-material/GrainOutlined'
import SaveIcon from '@mui/icons-material/SaveOutlined'
import CallToActionIcon from '@mui/icons-material/CallToActionOutlined'
import AppsIcon from '@mui/icons-material/AppsOutlined'
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt'
import PolylineOutlinedIcon from '@mui/icons-material/PolylineOutlined'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined'
import BubbleChartOutlinedIcon from '@mui/icons-material/BubbleChartOutlined'

import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import Container from '@mui/system/Container'
import Link from '@mui/material/Link'

// Filter options for context types
export const CONTEXT_FILTER_OPTIONS = [
    { value: 'ALL', label: 'View All Graphs' },
    { value: 'FAVORITES', label: 'Favorite Graphs Only' },
    { value: 'STANDARD', label: 'Standard Text Graphs' },
    { value: '----', label: '----' },
    { value: 'PKM_MINDMAP', label: 'Ontologies & Mindmaps' },
    { value: 'PKM_WIKILINKS', label: 'Knowledge Graphs' },
    { value: 'MEMORY', label: 'LLM Memory Graphs' },
    { value: '----', label: '----' },
    { value: 'TXT', label: 'Pasted Text Graphs' },
    { value: 'PDF', label: 'PDF Document Graphs' },
    { value: 'CSV', label: 'CSV Spreadsheet Graphs' },
    { value: 'MD', label: 'Markdown & Obsidian Graphs' },
    { value: '----', label: '----' },
    { value: 'GOOGLE', label: 'Google Search Results' },
    { value: 'SEARCH_INTENT', label: 'Search Intent Graphs' },
    { value: 'SEO', label: 'SEO Graphs' },
    { value: 'NICHE', label: 'Market Niche Graphs' },
    { value: 'TWITTER', label: 'X (Twitter) Graphs' },
    { value: '----', label: '----' },
    { value: 'GEXF', label: 'Social Networks' },
    { value: 'SCIENCE', label: 'Scientific Journal Graphs' },
    { value: 'EVERNOTE', label: 'Evernote Notes Graphs' },
]

// LocalStorage key for persisting filter
const CONTEXT_FILTER_STORAGE_KEY = 'infranodus_context_filter'

// Helper to get context type for creating new graphs based on filter
export const getContextTypeFromFilter = filterValue => {
    // For ALL and FAVORITES, default to STANDARD
    if (filterValue === 'ALL' || filterValue === 'FAVORITES') {
        return 'STANDARD'
    }
    // For PKM_MINDMAP, use MINDMAP type
    if (filterValue === 'PKM_MINDMAP') {
        return 'ONTOLOGY'
    }
    // For MEMORY, use MEMORY type
    if (filterValue === 'MEMORY') {
        return 'MEMORY'
    }
    // For SEARCH_INTENT, use STANDARD (the prefix kwrds_ will be added to name)
    if (filterValue === 'SEARCH_INTENT') {
        return 'STANDARD'
    }
    // Otherwise, use the filter value as the context type
    return filterValue
}

// Helper to get name prefix for certain context types
export const getNamePrefixFromFilter = filterValue => {
    switch (filterValue) {
        case 'SEARCH_INTENT':
            return 'kwrds_'
        case 'PKM_MINDMAP':
            return 'pkm_'
        case 'SEO':
            return 'seo_'
        case 'NICHE':
            return 'niche_'
        default:
            return ''
    }
}

import useUserInfo from '../../hooks/useUserInfo'
import usePageInfo from '../../hooks/usePageInfo'

import { useSnackbar } from '../Snackbar'

import { UserContext } from '../../UserContext'
import {
    useContextsStore,
    selectIsPublicView,
} from '../../stores/useContextsStore'

import routes, { getContextRoute } from '../../routes'
import { appMarketingSiteLabel, isKeywordGraphApp } from '../../appVariant'

const selectorShowSnackbar = state => state.showSnackbar

import { replaceSpaceWithUnderscore, isStringSafe } from '../../utils/index'

import { sendToAnalytics } from '../../Analytics'

export const DrawerItems = React.memo(() => {
    const [isExpanded, expandContextList] = React.useState(true)
    const [creating, setCreating] = React.useState(false)
    const [searchQuery, setSearchQuery] = React.useState('')
    const [selectOpen, setSelectOpen] = React.useState(false)

    // Load filter from localStorage on mount, default to 'ALL'
    const [contextFilter, setContextFilter] = React.useState(() => {
        try {
            const saved = localStorage.getItem(CONTEXT_FILTER_STORAGE_KEY)
            return saved &&
                CONTEXT_FILTER_OPTIONS.some(opt => opt.value === saved)
                ? saved
                : 'ALL'
        } catch {
            return 'ALL'
        }
    })

    const { selected, location } = usePageInfo()
    const { username } = React.useContext(UserContext)
    const isPublic = useContextsStore(selectIsPublicView)

    // Ref for the create input container to scroll to
    const createInputRef = React.useRef(null)
    const inputFieldRef = React.useRef(null)

    // Ref for the graph-name search field, so we can keep the cursor in it
    // after the filter dropdown collapses on the first keystroke
    const searchInputRef = React.useRef(null)

    // When the dropdown collapses while a query is active, the MUI Select
    // focuses its own trigger on close. Re-claim focus for the search field
    // for a short window so it wins that race and the cursor stays put.
    React.useEffect(() => {
        if (selectOpen || !searchQuery) return
        let raf
        let frames = 0
        const tick = () => {
            searchInputRef.current?.focus()
            frames += 1
            if (frames < 20) raf = requestAnimationFrame(tick)
        }
        raf = requestAnimationFrame(tick)
        return () => cancelAnimationFrame(raf)
    }, [selectOpen, searchQuery])

    // Save filter to localStorage when it changes
    const handleFilterChange = React.useCallback(newFilter => {
        setContextFilter(newFilter)
        try {
            localStorage.setItem(CONTEXT_FILTER_STORAGE_KEY, newFilter)
        } catch {
            // localStorage not available
        }
    }, [])

    const handleClick = () => {
        expandContextList(!isExpanded)
        sendToAnalytics({
            userId: username,
            event: 'expand_graphs',
            data: { source: 'menu' },
        })
    }

    const handleCreate = React.useCallback(() => {
        setCreating(prev => !prev)
        sendToAnalytics({
            userId: username,
            event: 'create_new_graph',
            data: { source: 'menu' },
        })
    }, [username])

    // Handler for "Add New" button from ContextsList
    const handleAddNewFromList = React.useCallback(() => {
        // Open the create dialog if not already open
        if (!creating) {
            setCreating(true)
            sendToAnalytics({
                userId: username,
                event: 'create_new_graph',
                data: { source: 'context_list_add_button' },
            })
        }

        // Scroll to and focus the input field after a brief delay to allow rendering
        setTimeout(() => {
            if (createInputRef.current) {
                createInputRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                })
            }
            if (inputFieldRef.current) {
                inputFieldRef.current.focus()
            }
        }, 100)
    }, [creating, username])

    const onAppsClick = () => {
        sendToAnalytics({
            userId: username,
            event: 'go_to_apps',
            data: { source: 'menu' },
        })
    }

    const onImportClick = () => {
        sendToAnalytics({
            userId: username,
            event: 'go_to_import',
            data: { source: 'menu' },
        })
    }

    const onGraphSynthesisClick = () => {
        sendToAnalytics({
            userId: username,
            event: 'go_to_synthesis',
            data: { source: 'menu', destination: 'graphs' },
        })
    }

    const onConceptsSynthesisClick = () => {
        sendToAnalytics({
            userId: username,
            event: 'go_to_synthesis',
            data: { source: 'menu', destination: 'concepts' },
        })
    }

    const onSearchGraphsClick = () => {
        sendToAnalytics({
            userId: username,
            event: 'go_to_search',
            data: { source: 'menu' },
        })
    }

    const onAppsPage = location === 'apps'
    const onImportPage = location === 'import'
    const onSynthesisPage = location === 'synthesis'
    const onConceptsSynthesisPage = location === 'conceptsSynthesis'
    const onFindAllPage = location === 'search'

    const ContextsList = React.lazy(() => import('./ContextsList'))

    return (
        <>
            <Divider sx={{ mt: 6 }} />
            <ListItemButton
                component={Link}
                href={routes.landing}
                key="marketing-site-home"
            >
                <ListItemIcon>
                    <CallToActionIcon />
                </ListItemIcon>
                <ListItemText primary={appMarketingSiteLabel()} />
            </ListItemButton>
            <ListItemButton
                selected={onAppsPage}
                component={Link}
                href={routes.home}
                key="Apps"
                onClick={onAppsClick}
            >
                <ListItemIcon>
                    <AppsIcon />
                </ListItemIcon>
                <ListItemText primary="Apps" />
            </ListItemButton>
            <ListItemButton
                selected={onImportPage}
                component={Link}
                href={routes.import}
                key="Import Text"
                onClick={onImportClick}
            >
                <ListItemIcon>
                    <SystemUpdateAltIcon />
                </ListItemIcon>
                <ListItemText primary="Import Text" />
            </ListItemButton>
            <Divider sx={{ my: 1 }} />
            {!isPublic && [
                <ListItemButton onClick={handleCreate} key="Create a New Graph">
                    <ListItemIcon>
                        <AddCircleIcon />
                    </ListItemIcon>
                    <ListItemText primary="Create a New Graph" />
                </ListItemButton>,
            ]}
            {creating && (
                <NewNameInput
                    containerRef={createInputRef}
                    inputRef={inputFieldRef}
                    contextFilter={contextFilter}
                />
            )}
            <Divider sx={{ my: 1 }} />
            <ListItemButton
                component={Link}
                href={
                    !isPublic
                        ? routes.showAllUrl(username)
                        : routes.showAllUrlPublic(username)
                }
                selected={onSynthesisPage}
                key="Graph Synthesis"
                onClick={onGraphSynthesisClick}
            >
                <ListItemIcon>
                    <PolylineOutlinedIcon />
                </ListItemIcon>
                <ListItemText
                    primary={
                        !isPublic
                            ? 'Top Graphs Synthesis'
                            : 'Top Public Graphs Synthesis'
                    }
                />
            </ListItemButton>
            {!isKeywordGraphApp() && (
                <ListItemButton
                    component={Link}
                    href={
                        !isPublic
                            ? routes.showAllConceptsUrl(username)
                            : routes.showAllConceptsUrlPublic(username)
                    }
                    selected={onConceptsSynthesisPage}
                    key="Concepts Synthesis"
                    onClick={onConceptsSynthesisClick}
                >
                    <ListItemIcon>
                        <BubbleChartOutlinedIcon />
                    </ListItemIcon>
                    <ListItemText
                        primary={
                            !isPublic
                                ? 'Top Concepts Synthesis'
                                : 'Top Public Concepts Synthesis'
                        }
                    />
                </ListItemButton>
            )}
            {!isPublic && [
                <ListItemButton
                    component={Link}
                    href={routes.findAllUrl(username)}
                    selected={onFindAllPage}
                    key="Search All Graphs"
                    onClick={onSearchGraphsClick}
                >
                    <ListItemIcon>
                        <SearchOutlinedIcon />
                    </ListItemIcon>
                    <ListItemText primary="Search All Graphs" />
                </ListItemButton>,
            ]}
            <Divider sx={{ my: 1 }} />
            <ListItemButton
                sx={{ mr: 0, pr: 1 }}
                key="Graphs Folder"
                onClick={handleClick}
            >
                <ListItemIcon>
                    <GrainOutlinedIcon />
                </ListItemIcon>
                <ListItemText
                    primary={
                        isPublic ? "User's Public Graphs" : 'Your Text Graphs'
                    }
                />
                {isExpanded ? (
                    <ExpandLess sx={{ pr: 1 }} />
                ) : (
                    <ExpandMore sx={{ pr: 1 }} />
                )}
            </ListItemButton>
            {isExpanded && (
                <Box
                    sx={{
                        px: 2,
                        py: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                    }}
                >
                    <FormControl fullWidth size="small">
                        <InputLabel
                            id="context-filter-label"
                            sx={{ fontSize: '0.8rem' }}
                        >
                            Filter by Type
                        </InputLabel>
                        <Select
                            labelId="context-filter-label"
                            id="context-filter-select"
                            value={contextFilter}
                            label="Filter by Type"
                            open={selectOpen}
                            onOpen={() => setSelectOpen(true)}
                            onClose={() => setSelectOpen(false)}
                            onChange={e => handleFilterChange(e.target.value)}
                            sx={{
                                fontSize: '0.8rem',
                                '& .MuiSelect-select': {
                                    py: 1,
                                },
                            }}
                            MenuProps={{
                                autoFocus: false,
                                // Don't let the menu's focus trap / restore
                                // steal the cursor when it collapses — we hand
                                // focus to the search field instead
                                disableAutoFocus: true,
                                disableEnforceFocus: true,
                                disableRestoreFocus: true,
                                // Close instantly so the focus hand-off isn't
                                // fighting a close transition
                                transitionDuration: 0,
                                PaperProps: {
                                    sx: {
                                        maxHeight: 300,
                                    },
                                },
                            }}
                        >
                            {/* Search lives at the top of the dropdown menu */}
                            <Box
                                sx={{
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 1,
                                    bgcolor: 'background.paper',
                                    px: 1,
                                    pt: 0.5,
                                    pb: 1,
                                }}
                                // Keep typing/clicks in the field; don't let the
                                // Select hijack them to jump between options
                                onKeyDown={e => e.stopPropagation()}
                                onClick={e => e.stopPropagation()}
                                onMouseDown={e => e.stopPropagation()}
                            >
                                <TextField
                                    autoFocus
                                    fullWidth
                                    size="small"
                                    value={searchQuery}
                                    onChange={e => {
                                        setSearchQuery(e.target.value)
                                        // Collapse the menu so the filtered
                                        // graphs show directly underneath
                                        setSelectOpen(false)
                                    }}
                                    placeholder="Search graphs by name…"
                                    inputProps={{
                                        style: { fontSize: '0.8rem' },
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchOutlinedIcon
                                                    sx={{ fontSize: '1.1rem' }}
                                                />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>
                            {CONTEXT_FILTER_OPTIONS.map((option, index) =>
                                option.value === '----' ? (
                                    <Divider
                                        key={`divider-${index}`}
                                        sx={{ my: 0.5 }}
                                    />
                                ) : (
                                    <MenuItem
                                        key={option.value}
                                        value={option.value}
                                        sx={{ fontSize: '0.8rem' }}
                                    >
                                        {option.label}
                                    </MenuItem>
                                )
                            )}
                        </Select>
                    </FormControl>
                    {/* Once typing, surface the query right above the filtered
                        list so it stays visible/editable after the menu closes */}
                    {searchQuery && (
                        <TextField
                            autoFocus
                            inputRef={searchInputRef}
                            fullWidth
                            size="small"
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            placeholder="Search graphs by name…"
                            inputProps={{ style: { fontSize: '0.8rem' } }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchOutlinedIcon
                                            sx={{ fontSize: '1.1rem' }}
                                        />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            aria-label="clear search"
                                            onClick={() => setSearchQuery('')}
                                        >
                                            <ClearOutlinedIcon
                                                sx={{ fontSize: '1.1rem' }}
                                            />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    )}
                </Box>
            )}
            <React.Suspense
                fallback={
                    <List component="div">
                        <ListItem key="loading" label="Loading...">
                            <ListItemButton key="loading item">
                                <IconButton
                                    aria-label="live-graph"
                                    edge="end"
                                    sx={{
                                        marginRight: 1,
                                        marginLeft: 1,
                                        paddingLeft: 1,
                                        paddingRight: 1,
                                        paddingTop: 1,
                                        paddingBottom: 1,
                                        width: 4,
                                        height: 4,
                                        color: 'lime',
                                    }}
                                ></IconButton>

                                <IconButton
                                    aria-label="favorite-graph"
                                    edge="end"
                                    sx={{
                                        marginRight: 1,
                                        marginLeft: 1,
                                        paddingLeft: 1,
                                        paddingRight: 1,
                                        paddingTop: 1,
                                        paddingBottom: 1,
                                        color: 'white',
                                    }}
                                >
                                    <CircularProgress size={24} />
                                </IconButton>
                                <ListItemText
                                    sx={{
                                        paddingLeft: 1,
                                        paddingRight: 2,
                                    }}
                                    primaryTypographyProps={{
                                        sx: {
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        },
                                    }}
                                    primary={'Loading...'}
                                />
                            </ListItemButton>
                        </ListItem>
                    </List>
                }
            >
                {isExpanded && (
                    <ContextsList
                        filterType={contextFilter}
                        searchQuery={searchQuery}
                        onAddNew={handleAddNewFromList}
                        isPublicView={isPublic}
                    />
                )}
            </React.Suspense>
        </>
    )
})

const NewNameInput = ({ containerRef, inputRef, contextFilter = 'ALL' }) => {
    const showSnackbar = useSnackbar(selectorShowSnackbar)
    const { username } = useUserInfo()
    const [invalid, setInvalid] = React.useState(false)
    const localRef = React.useRef()

    // Use provided inputRef or fall back to local ref
    const actualInputRef = inputRef || localRef

    // Get the prefix based on the current filter
    const namePrefix = getNamePrefixFromFilter(contextFilter)
    const contextType = getContextTypeFromFilter(contextFilter)

    // Get label for the filter type
    const filterLabel =
        CONTEXT_FILTER_OPTIONS.find(opt => opt.value === contextFilter)
            ?.label || 'Graph'

    const onSave = () => {
        if (!Boolean(actualInputRef.current.value)) return setInvalid(true)

        let enteredName = replaceSpaceWithUnderscore(
            actualInputRef.current.value
        )

        // Add prefix if needed and not already present
        if (namePrefix && !enteredName.toLowerCase().startsWith(namePrefix)) {
            enteredName = namePrefix + enteredName
        }

        const newNamePassesTest = isStringSafe(enteredName)

        if (!newNamePassesTest) {
            showSnackbar(
                'error',
                `Please, only use latin letters, numbers, underscores, or dashes for the context name.`
            )
            return setInvalid(true)
        }

        // Build the URL with contextType parameter
        const link = getContextRoute(enteredName, '', contextType, username)
        const url = new URL(`${window.location.origin}/${link}`)

        // Add contextType as a query parameter if not STANDARD
        if (contextType && contextType !== 'STANDARD') {
            url.searchParams.set('mode', contextType)
        }

        window.location.href = url.toString()
    }

    // Focus input on mount
    React.useEffect(() => {
        if (actualInputRef.current) {
            actualInputRef.current.focus()
        }
    }, [])

    return (
        <Container
            ref={containerRef}
            style={{
                px: 2,
            }}
            sx={{
                pt: 1,
            }}
        >
            <TextField
                error={invalid}
                id="new-graph-name-input"
                label={
                    contextFilter === 'ALL' || contextFilter === 'FAVORITES'
                        ? 'Enter graph name'
                        : `New ${filterLabel
                              .replace(' Graphs', '')
                              .replace(' Only', '')} name`
                }
                placeholder={namePrefix ? `your_graph_name` : 'your_graph_name'}
                helperText={
                    invalid
                        ? 'Empty or wrong entry.'
                        : namePrefix
                        ? `Will be prefixed with "${namePrefix}"`
                        : null
                }
                onKeyDown={e => {
                    if (e.key === 'Enter') {
                        onSave()
                    }
                }}
                variant="outlined"
                fullWidth
                inputRef={actualInputRef}
                InputProps={{
                    endAdornment: (
                        <IconButton position="end" onClick={onSave}>
                            <SaveIcon />
                        </IconButton>
                    ),
                }}
            />
        </Container>
    )
}
